/**
 * Erkennt Logeinträge, die nur einen Netzabbruch beim Kunden beschreiben (kein Fehler im System).
 *
 * Anlass (Live Firma 30, 29.10.2026): Kommt die App aus dem Hintergrund zurück, bevor das Netz steht, scheitern
 * alle Startanfragen mit `status 0 / Failed to fetch`. Jede Stelle schrieb das per `console.error`, der
 * Console-Interceptor schickte es als **Error** an den Server — ein Funkloch ergab 7 „Störungen“ im Cockpit.
 * Solche Einträge werden jetzt als Warning (Kategorie Network) geloggt. Dass der Nutzer offline ist, zeigt
 * ihm das Banner `network-status-banner`; HttpService unterdrückt den Toast (`isNetworkError`).
 *
 * Bewusst eng: nur `HttpErrorResponse` mit Status 0 und die Fetch-Meldungen der Browser. Ein 4xx/5xx bleibt Error.
 */

const FETCH_FAILURE_MESSAGES = [
  'Failed to fetch', // Chrome, Android-WebView
  'Load failed', // Safari, iOS-WebView
  'NetworkError when attempting to fetch resource', // Firefox
  'The network connection was lost', // Safari
  'The Internet connection appears to be offline', // Safari
];

function isFetchFailureMessage(message: unknown): boolean {
  return (
    typeof message === 'string' &&
    // Lazy-Chunk nach Deployment ist kein Funkloch (eigene Behandlung im GlobalErrorHandler)
    !message.includes('dynamically imported module') &&
    FETCH_FAILURE_MESSAGES.some((m) => message.includes(m))
  );
}

/** Ein einzelner Wert (Fehlerobjekt, HttpErrorResponse oder dessen JSON-Form) ist ein Netzabbruch. */
export function isNetworkFailure(value: unknown): boolean {
  if (value === null || typeof value !== 'object') {
    return false;
  }
  const v = value as { name?: unknown; status?: unknown; message?: unknown };

  // HttpErrorResponse (auch als Klon/Plain-Objekt): Status 0 = Anfrage hat den Server nie erreicht
  if (v.name === 'HttpErrorResponse') {
    return v.status === 0;
  }

  // Roher fetch-Fehler
  return v.name === 'TypeError' && isFetchFailureMessage(v.message);
}

/** Irgendein Argument eines console.error-Aufrufs ist ein Netzabbruch. */
export function containsNetworkFailure(args: readonly unknown[]): boolean {
  return args.some((arg) => isNetworkFailure(arg));
}
